import Color from 'colorjs.io';
import type { HexOrRgbColorString } from 'theme-utils/types';

const DEFAULT_FORMAT = 'hex';

type ColorMixBaseOptions = {
  color: HexOrRgbColorString;
  /** Percentage to mix or adjust the color. Should be a whole number from 0–100. */
  percentage: number;
};

type Format = {
  format?: string;
};

type ColorMixOptions = ColorMixBaseOptions &
  Format & {
    mixColor: HexOrRgbColorString;
  };

/**
 * Mixes two colors together.
 * `percentage` is an amount from 0–100 and affects how much of the `mixColor` is added to the `color`.
 */
export function mix({
  color,
  percentage,
  mixColor,
  format = DEFAULT_FORMAT,
}: ColorMixOptions) {
  return new Color(color).mix(mixColor, percentage / 100).toString({ format });
}

/**
 * Darkens a color by mixing it with black.
 * `percentage` is an amount from 0–100 that black should be mixed in.
 */
export function shade({ color, percentage }: ColorMixBaseOptions) {
  return mix({ color, percentage, mixColor: '#000' });
}

/**
 * Lightens a color by mixing it with white.
 * `percentage` is an amount from 0–100 that white should be mixed in.
 */
export function tint({ color, percentage }: ColorMixBaseOptions) {
  return mix({ color, percentage, mixColor: '#fff' });
}

type ColorMixWithFormat = ColorMixBaseOptions & Format;

/**
 * Darkens a color by directly manipulating the `lightness` value.
 * `percentage` is an amount from 0–100 that the lightness should be lowered from its current amount.
 */
export function darken({
  color,
  percentage,
  format = DEFAULT_FORMAT,
}: ColorMixWithFormat) {
  return new Color(color)
    .to('hsl')
    .set({
      l: (l) => Math.max(l - percentage, 0),
    })
    .to('srgb')
    .toString({ format });
}

/**
 * Lightens a color by directly manipulating the `lightness` value.
 * `percentage` is an amount from 0–100 that the lightness should be increased from its current amount.
 */
export function lighten({
  color,
  percentage,
  format = DEFAULT_FORMAT,
}: ColorMixWithFormat) {
  return new Color(color)
    .to('hsl')
    .set({
      l: (l) => Math.min(l + percentage, 100),
    })
    .to('srgb')
    .toString({ format });
}

/**
 * Sets transparency for a color.
 * `percentage` is the amount from 0–100 that the color's current alpha value should be lowered
 */
export function transparentize({
  color,
  percentage,
  format = DEFAULT_FORMAT,
}: ColorMixWithFormat) {
  const colorObj = new Color(color).to('srgb');
  colorObj.alpha = Math.max(colorObj.alpha - percentage / 100, 0);
  return colorObj.toString({ format });
}

/**
 * Adjusts the hue of a color.
 * Degree is an amout from 0–360 that the color's hue should be shifted.
 */
export function adjustHue({ color, degree, format = DEFAULT_FORMAT }: any) {
  return new Color(color)
    .to('hsl')
    .set({
      h: (h) => h + degree,
    })
    .to('srgb')
    .toString({ format });
}

export const colorUtils = {
  adjustHue,
  darken,
  lighten,
  mix,
  shade,
  tint,
  transparentize,
};
